import React, { useEffect, useState, useRef, createElement, Component } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AgentAvatar } from './AgentAvatar';
import { ChatMessage as ChatMessageType } from '../../data/mockData';
import { KPICard } from './KPICard';
import { Card } from '../ui/Card';
import { ArtifactRenderer } from '../artifacts/ArtifactRenderer';
import { FileText, ExternalLink, Clock, Zap, AlertTriangle, ChevronRight, Database, ThumbsUp, ThumbsDown, RefreshCw, Copy, Check, Pencil, MoreVertical, FileDown, Flag, FileCode, Activity, AlertOctagon, BarChart3, Calendar, ListTodo, Layers, Terminal, Cpu, Sparkles } from 'lucide-react';
import { ConfluenceExportModal } from './ConfluenceExportModal';
import { SummarizeButton } from './SummarizeButton';
import { TokenUsagePopover } from './TokenUsagePopover';
import { formatTokens } from '../../services/tokenTracker';
interface ChatMessageProps {
  message: ChatMessageType;
  onFollowup?: (text: string) => void;
  isStreaming?: boolean;
  onFeedback?: (messageId: string, rating: 'up' | 'down') => void;
  onEdit?: (messageId: string) => void;
  onRegenerate?: (messageId: string) => void;
}
// Markdown components styled for dark theme chat bubbles
const markdownComponents = {
  h1: ({
    children,
    ...props
  }: any) => <h1 className="text-lg font-bold text-slate-100 mt-3 mb-2 first:mt-0" {...props} data-id="element-1881">
      {children}
    </h1>,
  h2: ({
    children,
    ...props
  }: any) => <h2 className="text-base font-bold text-slate-100 mt-3 mb-1.5 first:mt-0" {...props} data-id="element-1882">
      {children}
    </h2>,
  h3: ({
    children,
    ...props
  }: any) => <h3 className="text-sm font-bold text-slate-200 mt-2 mb-1 first:mt-0" {...props} data-id="element-1883">
      {children}
    </h3>,
  p: ({
    children,
    ...props
  }: any) => <p className="mb-2 last:mb-0 leading-relaxed" {...props} data-id="element-1884">
      {children}
    </p>,
  strong: ({
    children,
    ...props
  }: any) => <strong className="font-semibold text-slate-100" {...props} data-id="element-1885">
      {children}
    </strong>,
  em: ({
    children,
    ...props
  }: any) => <em className="italic text-slate-300" {...props} data-id="element-1886">
      {children}
    </em>,
  a: ({
    children,
    href,
    ...props
  }: any) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2 decoration-indigo-400/40 hover:decoration-indigo-300/60 transition-colors inline-flex items-center gap-1" {...props} data-id="element-1887">
      {children}
      <ExternalLink size={10} className="inline-block flex-shrink-0" data-id="element-1888" />
    </a>,
  ul: ({
    children,
    ...props
  }: any) => <ul className="mb-2 last:mb-0 space-y-1 ml-1" {...props} data-id="element-1889">
      {children}
    </ul>,
  ol: ({
    children,
    ...props
  }: any) => <ol className="mb-2 last:mb-0 space-y-1 ml-1 list-decimal list-inside" {...props} data-id="element-1890">
      {children}
    </ol>,
  li: ({
    children,
    ...props
  }: any) => <li className="flex items-start gap-2 text-sm" {...props} data-id="element-1891">
      <span className="text-indigo-400 mt-1.5 flex-shrink-0 text-[8px]" data-id="element-1892">●</span>
      <span className="flex-1" data-id="element-1893">{children}</span>
    </li>,
  code: ({
    children,
    className,
    ...props
  }: any) => {
    const isInline = !className;
    if (isInline) {
      return <code className="px-1.5 py-0.5 bg-slate-700/60 text-indigo-300 rounded text-xs font-mono" {...props} data-id="element-1894">
          {children}
        </code>;
    }
    return <code className="block bg-slate-900/80 border border-slate-700 rounded-lg p-3 text-xs font-mono text-slate-300 overflow-x-auto my-2" {...props} data-id="element-1895">
        {children}
      </code>;
  },
  pre: ({
    children,
    ...props
  }: any) => <pre className="my-2" {...props} data-id="element-1896">
      {children}
    </pre>,
  table: ({
    children,
    ...props
  }: any) => <div className="my-3 overflow-x-auto rounded-lg border border-slate-700" data-id="element-1897">
      <table className="w-full text-xs" {...props} data-id="element-1898">
        {children}
      </table>
    </div>,
  thead: ({
    children,
    ...props
  }: any) => <thead className="bg-slate-700/50" {...props} data-id="element-1899">
      {children}
    </thead>,
  tbody: ({
    children,
    ...props
  }: any) => <tbody className="divide-y divide-slate-700/50" {...props} data-id="element-1900">
      {children}
    </tbody>,
  tr: ({
    children,
    ...props
  }: any) => <tr className="hover:bg-slate-700/20 transition-colors" {...props} data-id="element-1901">
      {children}
    </tr>,
  th: ({
    children,
    ...props
  }: any) => <th className="px-3 py-2 text-left font-semibold text-slate-200 whitespace-nowrap" {...props} data-id="element-1902">
      {children}
    </th>,
  td: ({
    children,
    ...props
  }: any) => <td className="px-3 py-2 text-slate-300 whitespace-nowrap" {...props} data-id="element-1903">
      {children}
    </td>,
  blockquote: ({
    children,
    ...props
  }: any) => <blockquote className="border-l-2 border-indigo-500/50 pl-3 my-2 text-slate-400 italic" {...props} data-id="element-1904">
      {children}
    </blockquote>,
  hr: (props: any) => <hr className="border-slate-700 my-3" {...props} data-id="element-1905" />
};
// Strip MCP call JSON blobs that the backend sometimes appends to the response
function cleanContent(text: string): string {
  return text.replace(/\{[^{}]*"mcpType"[^{}]*\}/g, '').replace(/\{[^{}]*"operation"[^{}]*"success"[^{}]*\}/g, '').replace(/\n{3,}/g, '\n\n').trim();
}
// Detect progress/loading messages from the agentic loop
function isProgressMessage(content: string): boolean {
  return /^(🧠|🔧|✨|🔄)\s\*\*/.test(content.trim());
}
// Parse progress content into structured parts
function parseProgressContent(content: string): {
  phase: string;
  completed: string[];
  inProgress: string[];
} {
  const lines = content.split('\n').map(l => l.trim()).filter(Boolean);
  const phase = lines[0]?.replace(/\*\*/g, '').replace(/^(🧠|🔧|✨|🔄)\s*/, '').trim() || 'Procesando...';
  const completed: string[] = [];
  const inProgress: string[] = [];
  for (const line of lines.slice(1)) {
    const cleanLine = line.replace(/\*\*/g, '');
    const toolMatches = cleanLine.match(/`([^`]+)`/g);
    if (toolMatches) {
      const tools = toolMatches.map(t => t.replace(/`/g, ''));
      if (cleanLine.startsWith('✅')) completed.push(...tools);else if (cleanLine.startsWith('⏳')) inProgress.push(...tools);
    }
  }
  return {
    phase,
    completed,
    inProgress
  };
}
// Parse MCP call JSON string into a readable label
function parseMcpCall(raw: string): string {
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const type = parsed.mcpType || parsed.type || '';
    const op = parsed.operation || '';
    const success = parsed.success !== false;
    const retrieved = parsed.dataRetrieved || '';
    const label = [type, op].filter(Boolean).join(' · ');
    return `${label}${retrieved ? ` (${retrieved})` : ''}${!success ? ' ✗' : ''}`;
  } catch {
    // If it's already a plain string (not JSON), return as-is
    return typeof raw === 'string' && raw.length < 60 ? raw : '';
  }
}
export function ChatMessage({
  message,
  onFollowup,
  isStreaming,
  onFeedback,
  onEdit,
  onRegenerate
}: ChatMessageProps) {
  const isAgent = message.role === 'agent';
  const hasLowConfidence = message.intentConfidence !== undefined && message.intentConfidence < 0.5;
  const hasMcpCalls = Array.isArray(message.mcpCalls) && message.mcpCalls.length > 0;
  const hasFollowups = Array.isArray(message.suggestedFollowups) && message.suggestedFollowups.length > 0;
  const hasWarnings = Array.isArray(message.warnings) && message.warnings.length > 0;
  const [copied, setCopied] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showConfluenceExport, setShowConfluenceExport] = useState(false);
  const [showTokenUsage, setShowTokenUsage] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const copyToClipboard = (text: string) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(() => {
        fallbackCopy(text);
      });
    } else {
      fallbackCopy(text);
    }
  };
  const fallbackCopy = (text: string) => {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    try {
      document.execCommand('copy');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
    document.body.removeChild(ta);
  };
  const handleCopy = () => {
    copyToClipboard(message.content);
  };
  const handleCopyMarkdown = () => {
    setShowMoreMenu(false);
    copyToClipboard(message.content);
  };
  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
        setShowMoreMenu(false);
        setShowTokenUsage(false);
      }
    };
    if (showMoreMenu || showTokenUsage) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMoreMenu, showTokenUsage]);
  // ─── Special Welcome Layout ──────────────────────────────────────────
  if (message.id === 'welcome' && onFollowup) {
    const quickActions = [{
      icon: Activity,
      label: 'Estado del Sprint',
      color: 'text-emerald-400 bg-emerald-950/40 border-emerald-900/40',
      prompt: '¿Cuál es el estado del sprint actual?'
    }, {
      icon: AlertOctagon,
      label: 'Bloqueadores',
      color: 'text-red-400 bg-red-950/40 border-red-900/40',
      prompt: '¿Hay bloqueadores en el sprint?'
    }, {
      icon: BarChart3,
      label: 'Velocidad',
      color: 'text-blue-400 bg-blue-950/40 border-blue-900/40',
      prompt: 'Mostrame la velocidad del equipo'
    }, {
      icon: Calendar,
      label: 'Daily Standup',
      color: 'text-amber-400 bg-amber-950/40 border-amber-900/40',
      prompt: 'Preparame el daily standup'
    }, {
      icon: ListTodo,
      label: 'Backlog',
      color: 'text-violet-400 bg-violet-950/40 border-violet-900/40',
      prompt: 'Listame el backlog del proyecto'
    }, {
      icon: Layers,
      label: 'Épicas',
      color: 'text-indigo-400 bg-indigo-950/40 border-indigo-900/40',
      prompt: 'Mostrame las épicas del proyecto'
    }];
    return <div className="mb-8 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-1906">
        {/* Greeting */}
        <div className="text-center mb-6" data-id="element-1907">
          <AgentAvatar size="lg" data-id="element-1908" />
          <h2 className="text-xl font-bold text-slate-100 mt-4" data-id="element-1909">
            ¡Hola! Soy tu Scrum Master Virtual
          </h2>
          <p className="text-sm text-slate-400 mt-1.5 max-w-md mx-auto" data-id="element-1910">
            Conectado a Jira y Confluence. Elegí una acción rápida o escribí tu
            consulta.
          </p>
        </div>

        {/* Quick Action Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 max-w-lg mx-auto" data-id="element-1911">
          {quickActions.map(action => {
          const Icon = action.icon;
          return <button key={action.label} onClick={() => onFollowup(action.prompt)} className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-all duration-200 hover:scale-[1.03] hover:shadow-lg hover:shadow-black/20 active:scale-[0.98] ${action.color}`} data-id="element-1912">
                <Icon size={20} data-id="element-1913" />
                <span className="text-xs font-medium text-slate-200" data-id="element-1914">
                  {action.label}
                </span>
              </button>;
        })}
        </div>

        {/* Tip */}
        <div className="flex items-center justify-center gap-2 mt-5" data-id="element-1915">
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800/60 border border-slate-700/50 rounded-full" data-id="element-1916">
            <Terminal size={12} className="text-slate-500" data-id="element-1917" />
            <span className="text-[11px] text-slate-500" data-id="element-1918">
              Escribí{' '}
              <kbd className="px-1.5 py-0.5 bg-slate-700 rounded text-[10px] text-slate-300 font-mono mx-0.5" data-id="element-1919">
                /
              </kbd>{' '}
              para ver todos los comandos
            </span>
          </div>
        </div>
      </div>;
  }
  // ─── Progress/Loading Message — Fancy SlackPanel-style ──────────────
  if (isAgent && isProgressMessage(message.content)) {
    const progress = parseProgressContent(message.content);
    return <div className="flex gap-4 mb-6 animate-in fade-in slide-in-from-bottom-2 duration-300" data-id="element-1920">
        {/* Glowing Avatar */}
        <div className="flex-shrink-0" data-id="element-1921">
          <AgentAvatar size="md" thinking data-id="element-1922" />
        </div>

        {/* Progress Content */}
        <div className="flex flex-col items-start max-w-[85%]" data-id="element-1923">
          {/* Phase label + inline dots */}
          <div className="flex items-center gap-2 mb-1.5" data-id="element-1924">
            <span className="font-bold text-indigo-300 text-sm" data-id="element-1925">
              Workium Agent
            </span>
            <span className="text-[9px] bg-indigo-900/50 text-indigo-300 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider border border-indigo-800/50 animate-pulse" data-id="element-1926">
              {progress.phase}
            </span>
            {/* Inline bouncing dots */}
            <span className="flex gap-1 ml-1" data-id="element-1927">
              <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{
              animationDelay: '0ms'
            }} data-id="element-1928" />
              <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{
              animationDelay: '150ms'
            }} data-id="element-1929" />
              <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{
              animationDelay: '300ms'
            }} data-id="element-1930" />
            </span>
          </div>

          {/* Tool calls progress */}
          {(progress.completed.length > 0 || progress.inProgress.length > 0) && <div className="space-y-1" data-id="element-1931">
              {progress.completed.map((tool, i) => <div key={`c-${i}`} className="flex items-center gap-1.5 text-[11px]" data-id="element-1932">
                  <Check size={11} className="text-emerald-400" data-id="element-1933" />
                  <span className="text-emerald-400/80 font-mono bg-emerald-950/30 px-1.5 py-0.5 rounded border border-emerald-900/30" data-id="element-1934">
                    {tool}
                  </span>
                </div>)}
              {progress.inProgress.map((tool, i) => <div key={`p-${i}`} className="flex items-center gap-1.5 text-[11px]" data-id="element-1935">
                  <Sparkles size={11} className="text-indigo-400 animate-pulse" data-id="element-1936" />
                  <span className="text-indigo-400/80 font-mono bg-indigo-950/30 px-1.5 py-0.5 rounded border border-indigo-900/30 animate-pulse" data-id="element-1937">
                    {tool}
                  </span>
                </div>)}
            </div>}
        </div>
      </div>;
  }
  return <div className={`flex gap-4 group ${isAgent ? 'flex-row' : 'flex-row-reverse'} mb-6 animate-in fade-in slide-in-from-bottom-2 duration-300`} data-id="element-1938">
      {/* Avatar */}
      <div className="flex-shrink-0" data-id="element-1939">
        {isAgent ? <AgentAvatar size="md" data-id="element-1940" /> : <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-xs shadow-lg shadow-indigo-900/20" data-id="element-1941">
            YO
          </div>}
      </div>

      {/* Content */}
      <div className={`flex flex-col max-w-[85%] ${isAgent ? 'items-start' : 'items-end'}`} data-id="element-1942">
        {/* Low confidence warning */}
        {isAgent && hasLowConfidence && <div className="flex items-center gap-1.5 text-[10px] text-amber-400 mb-1.5 px-1" data-id="element-1943">
            <AlertTriangle size={10} data-id="element-1944" />
            <span data-id="element-1945">Confianza baja — intentá reformular</span>
          </div>}

        {/* Main bubble */}
        <div className={`px-5 py-3.5 rounded-2xl shadow-sm text-sm leading-relaxed ${isAgent ? 'bg-slate-800 border border-slate-700 text-slate-300 rounded-tl-none' : 'bg-indigo-600 text-white rounded-tr-none shadow-md shadow-indigo-900/20'}`} data-id="element-1946">
          {isAgent ? <div className="prose-chat" data-id="element-1947">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents} data-id="element-1948">
                {cleanContent(message.content)}
              </ReactMarkdown>
            </div> : <span data-id="element-1949">{message.content}</span>}
          {isStreaming && <span className="inline-block w-0.5 h-4 bg-indigo-400 ml-0.5 animate-pulse align-middle" data-id="element-1950" />}
        </div>

        {/* Meta row: timestamp + latency + tokens + MCP calls */}
        <div className={`flex items-center gap-3 mt-1 px-1 ${isAgent ? 'flex-row' : 'flex-row-reverse'}`} data-id="element-1951">
          <span className="text-xs text-slate-500" data-id="element-1952">{message.timestamp}</span>

          {isAgent && message.latencyMs !== undefined && message.latencyMs > 0 && <span className="flex items-center gap-1 text-[10px] text-slate-600" data-id="element-1953">
                <Clock size={9} data-id="element-1954" />
                {message.latencyMs}ms
              </span>}

          {isAgent && message.tokenUsage && <span className="flex items-center gap-1 text-[10px] text-slate-600" data-id="element-1955">
              <Cpu size={9} data-id="element-1956" />
              <span className="text-emerald-500/70" data-id="element-1957">
                ↑{formatTokens(message.tokenUsage.total_prompt_tokens)}
              </span>
              <span className="text-blue-500/70" data-id="element-1958">
                ↓{formatTokens(message.tokenUsage.total_completion_tokens)}
              </span>
            </span>}

          {isAgent && Array.isArray(message.mcpCalls) && message.mcpCalls.length > 0 && <div className="flex items-center gap-1.5 flex-wrap" data-id="element-1959">
                <Database size={9} className="text-indigo-500" data-id="element-1960" />
                {message.mcpCalls.slice(0, 3).map((call, idx) => {
            const label = parseMcpCall(call as any);
            if (!label) return null;
            return <span key={idx} className="text-[10px] text-indigo-500/70 bg-indigo-950/30 px-1.5 py-0.5 rounded border border-indigo-900/30" data-id="element-1961">
                      {label}
                    </span>;
          })}
                {message.mcpCalls.length > 3 && <span className="text-[10px] text-slate-600" data-id="element-1962">
                    +{message.mcpCalls.length - 3}
                  </span>}
              </div>}
        </div>

        {/* Action buttons — Agent messages */}
        {isAgent && !isStreaming && message.id !== 'welcome' && <div className="flex items-center gap-0.5 mt-1.5 px-1 opacity-60 group-hover:opacity-100 transition-opacity duration-200" data-id="element-1963">
            <button onClick={() => onFeedback?.(message.id, 'up')} className={`p-1.5 rounded-md transition-all duration-150 ${message.feedback === 'up' ? 'text-emerald-400 bg-emerald-950/40' : 'text-slate-600 hover:text-slate-300 hover:bg-slate-800'}`} title="Buena respuesta" data-id="element-1964">
              <ThumbsUp size={13} strokeWidth={message.feedback === 'up' ? 2.5 : 1.5} data-id="element-1965" />
            </button>
            <button onClick={() => onFeedback?.(message.id, 'down')} className={`p-1.5 rounded-md transition-all duration-150 ${message.feedback === 'down' ? 'text-red-400 bg-red-950/40' : 'text-slate-600 hover:text-slate-300 hover:bg-slate-800'}`} title="Mala respuesta" data-id="element-1966">
              <ThumbsDown size={13} strokeWidth={message.feedback === 'down' ? 2.5 : 1.5} data-id="element-1967" />
            </button>
            <div className="w-px h-3.5 bg-slate-800 mx-1" data-id="element-1968" />
            <button onClick={handleCopy} className="p-1.5 rounded-md text-slate-600 hover:text-slate-300 hover:bg-slate-800 transition-all duration-150" title="Copiar respuesta" data-id="element-1969">
              {copied ? <Check size={13} className="text-emerald-400" data-id="element-1970" /> : <Copy size={13} data-id="element-1971" />}
            </button>
            <button onClick={() => onRegenerate?.(message.id)} className="p-1.5 rounded-md text-slate-600 hover:text-slate-300 hover:bg-slate-800 transition-all duration-150" title="Regenerar respuesta" data-id="element-1972">
              <RefreshCw size={13} data-id="element-1973" />
            </button>
            {message.content.length > 500 && <SummarizeButton content={message.content} title="Resumen del mensaje" contextType="mensaje del asistente" className="ml-1 !px-2 !py-1 !text-[10px] !bg-transparent !border-none hover:!bg-slate-800 !text-slate-500 hover:!text-indigo-400" data-id="element-1974" />}
            {/* More menu */}
            <div className="relative" ref={moreMenuRef} data-id="element-1975">
              <button onClick={() => setShowMoreMenu(!showMoreMenu)} className={`p-1.5 rounded-md transition-all duration-150 ${showMoreMenu ? 'text-slate-300 bg-slate-800' : 'text-slate-600 hover:text-slate-300 hover:bg-slate-800'}`} title="Más opciones" data-id="element-1976">
                <MoreVertical size={13} data-id="element-1977" />
              </button>
              {showMoreMenu && <div className="absolute left-0 bottom-full mb-1 w-52 bg-slate-800 border border-slate-700 rounded-lg shadow-xl shadow-black/50 py-1 z-50 animate-in fade-in slide-in-from-bottom-2 duration-150" data-id="element-1978">
                  <button onClick={handleCopyMarkdown} className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-slate-300 hover:bg-slate-700 hover:text-slate-100 transition-colors" data-id="element-1979">
                    <FileCode size={13} className="text-slate-500" data-id="element-1980" />
                    Copiar como Markdown
                  </button>
                  <button onClick={() => {
              setShowMoreMenu(false);
              setShowConfluenceExport(true);
            }} className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-slate-300 hover:bg-slate-700 hover:text-slate-100 transition-colors" data-id="element-1981">
                    <FileDown size={13} className="text-slate-500" data-id="element-1982" />
                    Exportar a Confluence
                  </button>
                  {message.tokenUsage && <button onClick={() => {
              setShowMoreMenu(false);
              setShowTokenUsage(true);
            }} className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-slate-300 hover:bg-slate-700 hover:text-slate-100 transition-colors" data-id="element-1983">
                      <Cpu size={13} className="text-slate-500" data-id="element-1984" />
                      Ver uso de tokens
                    </button>}
                  <div className="h-px bg-slate-700 my-1" data-id="element-1985" />
                  <button onClick={() => {
              setShowMoreMenu(false);
              onFeedback?.(message.id, 'down');
            }} className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-slate-400 hover:bg-red-950/30 hover:text-red-400 transition-colors" data-id="element-1986">
                    <Flag size={13} className="text-slate-600" data-id="element-1987" />
                    Reportar problema
                  </button>
                </div>}
              {/* Token Usage Popover */}
              {showTokenUsage && message.tokenUsage && <div className="absolute right-0 bottom-full mb-2 z-50" data-id="element-1988">
                  <TokenUsagePopover usage={message.tokenUsage} onClose={() => setShowTokenUsage(false)} data-id="element-1989" />
                </div>}
            </div>
          </div>}

        {/* Action buttons — User messages: Copy + Edit */}
        {!isAgent && !isStreaming && <div className="flex items-center gap-0.5 mt-1 px-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200" data-id="element-1990">
            <button onClick={handleCopy} className="p-1.5 rounded-md text-slate-600 hover:text-slate-300 hover:bg-slate-800 transition-all duration-150" title="Copiar mensaje" data-id="element-1991">
              {copied ? <Check size={13} className="text-emerald-400" data-id="element-1992" /> : <Copy size={13} data-id="element-1993" />}
            </button>
            <button onClick={() => onEdit?.(message.id)} className="p-1.5 rounded-md text-slate-600 hover:text-slate-300 hover:bg-slate-800 transition-all duration-150" title="Editar mensaje" data-id="element-1994">
              <Pencil size={13} data-id="element-1995" />
            </button>
          </div>}

        {/* Warnings */}
        {isAgent && Array.isArray(message.warnings) && message.warnings.length > 0 && <div className="mt-2 space-y-1 w-full" data-id="element-1996">
              {message.warnings.map((w, i) => <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-amber-950/30 border border-amber-900/40 rounded-lg text-xs text-amber-400" data-id="element-1997">
                  <AlertTriangle size={11} data-id="element-1998" />
                  {w}
                </div>)}
            </div>}

        {/* Artifacts (New rich visual components) */}
        {isAgent && Array.isArray(message.artifacts) && message.artifacts.length > 0 && <div className="mt-3 w-full space-y-3" data-id="element-1999">
              {message.artifacts.map(artifact => <ArtifactRenderer key={artifact.id} artifact={artifact} data-id="element-2000" />)}
            </div>}

        {/* Legacy Attachments */}
        {isAgent && Array.isArray(message.attachments) && message.attachments.length > 0 && <div className="mt-3 space-y-3 w-full" data-id="element-2001">
              {message.attachments.map((att, idx) => {
          if (att.type === 'kpi') {
            return <div key={idx} className="w-64" data-id="element-2002">
                      <KPICard kpi={att.data} data-id="element-2003" />
                    </div>;
          }
          if (att.type === 'blockers') {
            return <Card key={idx} className="w-full max-w-md bg-red-950/30 border-red-900/50" data-id="element-2004">
                      <div className="p-4" data-id="element-2005">
                        <h4 className="text-red-400 font-semibold mb-2 flex items-center gap-2" data-id="element-2006">
                          <AlertTriangle size={14} data-id="element-2007" /> Bloqueadores Detectados
                        </h4>
                        <ul className="space-y-2" data-id="element-2008">
                          {att.data.map((b: any) => <li key={b.id} className="text-sm bg-slate-900 p-2 rounded border border-red-900/30" data-id="element-2009">
                              <div className="font-medium text-slate-200" data-id="element-2010">
                                {b.issueKey}: {b.description}
                              </div>
                              <div className="text-xs text-slate-500 mt-1 flex justify-between" data-id="element-2011">
                                <span data-id="element-2012">Asignado: {b.owner.name}</span>
                                <span className="text-red-400 font-medium" data-id="element-2013">
                                  {b.age} antigüedad
                                </span>
                              </div>
                            </li>)}
                        </ul>
                      </div>
                    </Card>;
          }
          if (att.type === 'decision') {
            return <Card key={idx} className="w-full max-w-md bg-blue-950/30 border-blue-900/50" data-id="element-2014">
                      <div className="p-4 flex items-start gap-3" data-id="element-2015">
                        <div className="p-2 bg-blue-900/50 rounded-lg text-blue-400" data-id="element-2016">
                          <FileText size={20} data-id="element-2017" />
                        </div>
                        <div className="flex-1" data-id="element-2018">
                          <h4 className="text-blue-200 font-semibold" data-id="element-2019">
                            Decision Log Created
                          </h4>
                          <p className="text-sm text-blue-300/80 mt-1" data-id="element-2020">
                            {att.data.title}
                          </p>
                          <div className="flex items-center gap-2 mt-2" data-id="element-2021">
                            <span className="text-xs px-2 py-0.5 bg-blue-900/50 text-blue-300 rounded-full uppercase font-bold tracking-wider border border-blue-800" data-id="element-2022">
                              {att.data.status}
                            </span>
                            <a href="#" className="text-xs text-blue-400 hover:text-blue-300 hover:underline flex items-center gap-1" data-id="element-2023">
                              Ver en Confluence <ExternalLink size={10} data-id="element-2024" />
                            </a>
                          </div>
                        </div>
                      </div>
                    </Card>;
          }
          if (att.type === 'sprint_summary') {
            return <Card key={idx} className="w-full max-w-md bg-slate-800 border-slate-700" data-id="element-2025">
                      <div className="p-4" data-id="element-2026">
                        <h4 className="font-semibold text-slate-200 mb-1" data-id="element-2027">
                          {att.data.name}
                        </h4>
                        <p className="text-sm text-slate-400 mb-3" data-id="element-2028">
                          {att.data.goal}
                        </p>
                        <div className="w-full bg-slate-700 rounded-full h-2 mb-1" data-id="element-2029">
                          <div className="bg-indigo-500 h-2 rounded-full" style={{
                    width: `${att.data.progress}%`
                  }} data-id="element-2030"></div>
                        </div>
                        <div className="flex justify-between text-xs text-slate-500" data-id="element-2031">
                          <span data-id="element-2032">{att.data.progress}% Completado</span>
                          <span data-id="element-2033">
                            {att.data.completedPoints}/{att.data.totalPoints}{' '}
                            pts
                          </span>
                        </div>
                      </div>
                    </Card>;
          }
          return null;
        })}
            </div>}

        {/* Suggested Followups */}
        {isAgent && Array.isArray(message.suggestedFollowups) && message.suggestedFollowups.length > 0 && onFollowup && <div className="mt-3 flex flex-wrap gap-2" data-id="element-2034">
              {message.suggestedFollowups.map((followup, idx) => <button key={idx} onClick={() => onFollowup(followup)} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-indigo-900/30 border border-slate-700 hover:border-indigo-500/50 rounded-full text-xs text-slate-400 hover:text-indigo-300 transition-all" data-id="element-2035">
                  <ChevronRight size={11} data-id="element-2036" />
                  {followup}
                </button>)}
            </div>}
      </div>

      {/* Confluence Export Modal */}
      {showConfluenceExport && <ConfluenceExportModal content={message.content} onClose={() => setShowConfluenceExport(false)} data-id="element-2037" />}
    </div>;
}